import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Paper,
  styled,
} from "@mui/material";
import {
  Email as EmailIcon,
  LockReset as LockResetIcon,
  ArrowBack as ArrowBackIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import MainHeader from "../../components/MainHeader";
import HeaderSecti from "../../components/HeaderSecti";
import Rodape from "../../components/Rodape";
import ResponsiveModal from "../../components/ResponsiveModal";

import api from "../../services/api";
import history from "../../services/history";

// ---------------------------------------------------------------------------
// Tela /forgot no redesenho (mockups de 08/09/2026): duas colunas — painel
// explicativo à esquerda, cartão branco com o formulário à direita — na mesma
// linguagem de SignIn/SignUpAluno/SelectSignUp (fundo creme, círculos
// decorativos, campos de 54px, Poppins).
//
// ⚠️ O TEXTO DIVERGE DO MOCKUP DE PROPÓSITO. O mockup descreve um fluxo de
// "link seguro válido por 30 minutos", que NÃO EXISTE: o
// `ForgotPasswordController` gera uma senha aleatória com
// `crypto.randomBytes(4)`, grava o hash na hora e manda a senha por e-mail
// ("Sua nova senha é: ..."), nas duas rotas (aluno e empresa). Não há token,
// link nem expiração em nenhum lugar do backend. Escrever o texto do mockup
// seria prometer ao usuário algo falso num fluxo de segurança, então os três
// passos descrevem o fluxo real. Decisão do Marcelo em 08/09/2026: subir o
// visual agora com texto honesto.
//
// ORDEM DO ARQUIVO: FONTE e COR são lidos em tempo de avaliação do módulo pelos
// styled() abaixo — declarar depois deles dá ReferenceError em runtime (o build
// compila e a tela fica branca).
// ---------------------------------------------------------------------------
const FONTE = "'Poppins', system-ui, sans-serif";

const COR = {
  fundo: "#FFF9EF",
  cartao: "#FFFFFF",
  linha: "#EDE4D6",
  tinta: "#1F1E1C",
  textoSuave: "#6B655D",
  apagado: "#8C8478",
  rotulo: "#3B3833",
  campoBorda: "#E2DACD",
  campoFundo: "#FDFBF7",
  laranja: "#D2551D",
  laranjaHover: "#B8460F",
  laranjaClaro: "#FBE7DB",
  azul: "#2C5FA8",
  azulHover: "#24518F",
  azulClaro: "#E3ECF9",
  creme: "#FDF6E7",
  pessego: "#FBEADF",
};

const CampoTexto = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    minHeight: 54,
    borderRadius: 11,
    backgroundColor: COR.campoFundo,
    fontFamily: FONTE,
    fontSize: 15.5,
    color: COR.tinta,
    transition: "background-color .15s, box-shadow .15s",
    "& fieldset": { borderWidth: 1.5, borderColor: COR.campoBorda },
    "&:hover fieldset": { borderColor: COR.campoBorda },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      boxShadow: "0 0 0 4px rgba(44, 95, 168, .13)",
    },
    "&.Mui-focused fieldset": { borderWidth: 1.5, borderColor: COR.azul },
    "&.Mui-error fieldset": { borderColor: COR.laranja },
    "& legend": { display: "none" },
  },
  "& .MuiInputBase-input": {
    height: 54,
    boxSizing: "border-box",
    padding: "0 8px",
    fontFamily: FONTE,
    "&::placeholder": { color: "#A79E92", opacity: 1 },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: FONTE,
    fontSize: 12.5,
    marginLeft: 2,
    marginTop: 6,
    "&.Mui-error": { color: COR.laranja },
  },
});

// Passos do fluxo REAL (ver o comentário no topo do arquivo).
const PASSOS = [
  "Você recebe um e-mail com uma senha provisória",
  "Entra na plataforma com essa senha",
  "Troca por uma senha sua em Minha conta",
];

const ABAS = [
  {
    valor: "0",
    titulo: "Pessoa física",
    rotuloEmail: "E-mail cadastrado",
    rota: "forgotpassword/aluno",
    cor: COR.laranja,
    corHover: COR.laranjaHover,
    corSuave: COR.laranjaClaro,
  },
  {
    valor: "1",
    titulo: "Pessoa jurídica",
    rotuloEmail: "E-mail do responsável pela empresa",
    rota: "forgotpassword/empresa",
    cor: COR.azul,
    corHover: COR.azulHover,
    corSuave: COR.azulClaro,
  },
];

function Circulo({ cor, tamanho, ...pos }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        width: tamanho,
        height: tamanho,
        borderRadius: "50%",
        bgcolor: cor,
        pointerEvents: "none",
        ...pos,
      }}
    />
  );
}

export default function ForgotPassword() {
  // Logado, o layout já põe o cabeçalho da plataforma — o público empilhava.
  const signed = useSelector((s) => s?.auth?.signed);
  const [tabValue, setTabValue] = useState("0");
  const [successOpen, setSuccessOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const aba = ABAS.find((a) => a.valor === tabValue) || ABAS[0];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mesmas validações do formulário anterior.
    if (!email?.trim()) {
      setEmailError("Campo obrigatório.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Email inválido!");
      return;
    }
    setEmailError("");

    setLoading(true);
    try {
      await api.patch(aba.rota, { email: email.trim() });
      setSuccessOpen(true);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Erro ao recuperar senha.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
    history.push("/login");
  };

  const trocarAba = (valor) => {
    setTabValue(valor);
    setEmailError("");
  };

  return (
    <>
      {!signed && (
        <>
          <HeaderSecti />
          <MainHeader page="login" />
        </>
      )}

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: COR.fundo,
          minHeight: { xs: "auto", md: "76vh" },
          width: "100%",
          py: { xs: 4, md: 8 },
          px: { xs: 2, md: 6 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONTE,
        }}
      >
        <Circulo cor={COR.creme} tamanho={340} top={-90} left={-120} />
        <Circulo cor={COR.pessego} tamanho={420} bottom={-190} right={-140} />

        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: 1180,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 4, md: 8 },
            alignItems: "center",
          }}
        >
          {/* Painel explicativo */}
          <Box>
            <Typography
              sx={{
                fontFamily: FONTE,
                fontSize: 12.5,
                fontWeight: 700,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                color: COR.azul,
                mb: 1,
              }}
            >
              Recuperação de acesso
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontFamily: FONTE,
                fontSize: { xs: 34, md: 46 },
                fontWeight: 700,
                color: COR.laranja,
                lineHeight: 1.1,
                mb: 2.5,
              }}
            >
              Esqueceu a senha?
            </Typography>
            <Typography
              sx={{
                fontFamily: FONTE,
                fontSize: 17,
                color: COR.rotulo,
                lineHeight: 1.6,
                maxWidth: 460,
                mb: 3.5,
              }}
            >
              Sem problemas. Informe o e-mail cadastrado na plataforma{" "}
              <strong>OxeTech</strong> e enviaremos uma senha provisória para
              você entrar.
            </Typography>

            <Stack spacing={2} sx={{ mb: 3.5 }}>
              {PASSOS.map((passo, i) => (
                <Stack key={passo} direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      flexShrink: 0,
                      borderRadius: "50%",
                      bgcolor: COR.laranjaClaro,
                      color: COR.laranjaHover,
                      fontFamily: FONTE,
                      fontSize: 14,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography
                    sx={{ fontFamily: FONTE, fontSize: 15.5, color: COR.rotulo }}
                  >
                    {passo}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Typography sx={{ fontFamily: FONTE, fontSize: 15.5, color: COR.textoSuave }}>
              Lembrou da senha?{" "}
              <Link
                to="/login"
                style={{
                  color: COR.laranja,
                  fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                Fazer login
              </Link>
            </Typography>
          </Box>

          {/* Cartão do formulário */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              border: `1px solid ${COR.linha}`,
              bgcolor: COR.cartao,
              boxShadow: "0 18px 48px rgba(31,30,28,.07)",
              maxWidth: 520,
              width: "100%",
              mx: "auto",
            }}
          >
            <Typography
              sx={{
                fontFamily: FONTE,
                fontSize: 27,
                fontWeight: 700,
                color: COR.tinta,
                textAlign: "center",
              }}
            >
              Recuperar senha
            </Typography>
            <Typography
              sx={{
                fontFamily: FONTE,
                fontSize: 15,
                color: COR.textoSuave,
                textAlign: "center",
                mt: 0.75,
                mb: 3,
              }}
            >
              Escolha o tipo de conta e informe o e-mail
            </Typography>

            {/* Seletor em pílula */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.5,
                p: 0.5,
                mb: 3,
                borderRadius: 2.5,
                bgcolor: COR.creme,
                border: `1px solid ${COR.linha}`,
              }}
            >
              {ABAS.map((a) => {
                const ativa = a.valor === tabValue;
                return (
                  <Button
                    key={a.valor}
                    onClick={() => trocarAba(a.valor)}
                    disableRipple
                    sx={{
                      py: 1.15,
                      borderRadius: 2,
                      fontFamily: FONTE,
                      fontSize: 15,
                      fontWeight: ativa ? 700 : 600,
                      textTransform: "none",
                      color: ativa ? a.cor : COR.textoSuave,
                      bgcolor: ativa ? "#fff" : "transparent",
                      boxShadow: ativa ? "0 1px 4px rgba(31,30,28,.10)" : "none",
                      "&:hover": {
                        bgcolor: ativa ? "#fff" : "rgba(255,255,255,.55)",
                      },
                    }}
                  >
                    {a.titulo}
                  </Button>
                );
              })}
            </Box>

            <form onSubmit={handleSubmit} noValidate>
              <Typography
                sx={{
                  fontFamily: FONTE,
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: COR.rotulo,
                  mb: 0.75,
                }}
              >
                {aba.rotuloEmail}
              </Typography>
              <CampoTexto
                fullWidth
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError("");
                }}
                error={!!emailError}
                helperText={emailError}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: COR.apagado, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                startIcon={!loading && <LockResetIcon />}
                sx={{
                  mt: 2.5,
                  py: 1.6,
                  borderRadius: 2.5,
                  fontFamily: FONTE,
                  fontSize: 16,
                  fontWeight: 700,
                  textTransform: "none",
                  color: "#fff",
                  bgcolor: aba.cor,
                  boxShadow: "none",
                  "&:hover": { bgcolor: aba.corHover, boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: aba.corSuave, color: "#fff" },
                }}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  "Enviar senha provisória"
                )}
              </Button>
            </form>

            {/* Divisor "ou" */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 2.5 }}>
              <Box sx={{ flex: 1, height: 1, bgcolor: COR.linha }} />
              <Typography sx={{ fontFamily: FONTE, fontSize: 13.5, color: COR.apagado }}>
                ou
              </Typography>
              <Box sx={{ flex: 1, height: 1, bgcolor: COR.linha }} />
            </Stack>

            <Button
              component={Link}
              to={signed ? "/dashboard" : "/login"}
              fullWidth
              startIcon={<ArrowBackIcon />}
              sx={{
                py: 1.5,
                borderRadius: 2.5,
                fontFamily: FONTE,
                fontSize: 15.5,
                fontWeight: 700,
                textTransform: "none",
                color: COR.azul,
                border: `1.5px solid ${COR.azul}`,
                "&:hover": { bgcolor: COR.azulClaro },
              }}
            >
              {signed ? "Voltar ao meu painel" : "Voltar para o login"}
            </Button>

            {/* Pé: quem perdeu o acesso ao e-mail vai para a Central de ajuda,
                que explica as duas saídas (trocar o e-mail sabendo a senha, ou
                falar com a equipe). */}
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="flex-start"
              justifyContent="center"
              sx={{ mt: 2.5 }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, color: COR.apagado, mt: "2px" }} />
              <Typography
                sx={{
                  fontFamily: FONTE,
                  fontSize: 13,
                  color: COR.apagado,
                  textAlign: "center",
                  lineHeight: 1.5,
                }}
              >
                Não tem mais acesso ao e-mail?{" "}
                <Link
                  to="/ajuda"
                  style={{ color: COR.azul, fontWeight: 600, textDecoration: "none" }}
                >
                  Preciso de ajuda
                </Link>
              </Typography>
            </Stack>
          </Paper>
        </Box>
      </Box>

      <Rodape />

      <ResponsiveModal
        open={successOpen}
        onClose={handleSuccessClose}
        title="Senha provisória enviada"
        actions={
          <Button onClick={handleSuccessClose} autoFocus sx={{ fontFamily: FONTE }}>
            OK
          </Button>
        }
      >
        <Typography sx={{ fontFamily: FONTE }}>
          Enviamos uma senha provisória para o seu e-mail. Entre com ela e troque
          por uma senha sua em Minha conta. Se não encontrar a mensagem, verifique
          a caixa de SPAM.
        </Typography>
      </ResponsiveModal>
    </>
  );
}
